import { mappedColors } from '@pixee/design-system'
import classNames from 'classnames'
import { match } from 'ts-pattern'
import { z } from 'zod'
import * as styles from './pulsing-dot.css.ts'

export const PulsingDotVariantSchema = z.enum(['in-progress', 'on-deck'])
export type PulsingDotVariant = z.infer<typeof PulsingDotVariantSchema>

type PulsingDotProps = {
  variant: PulsingDotVariant
  size?: number
  className?: string
}

export const PulsingDot: React.FC<PulsingDotProps> = ({ variant, size = 24, className }) => {
  const config = match(variant)
    .with('in-progress', () => ({
      viewBox: '0 0 17 16',
      color: mappedColors.secondary.haze,
      innerPath:
        'M8.5 4C9.56087 4 10.5783 4.42143 11.3284 5.17157C12.0786 5.92172 12.5 6.93913 12.5 8C12.5 9.06087 12.0786 10.0783 11.3284 10.8284C10.5783 11.5786 9.56087 12 8.5 12C7.43913 12 6.42172 11.5786 5.67157 10.8284C4.92143 10.0783 4.5 9.06087 4.5 8C4.5 6.93913 4.92143 5.92172 5.67157 5.17157C6.42172 4.42143 7.43913 4 8.5 4Z',
      outerPath:
        'M12.0355 4.46447C11.0979 3.52678 9.82608 3 8.5 3C7.17392 3 5.90215 3.52678 4.96447 4.46447C4.02678 5.40215 3.5 6.67392 3.5 8C3.5 9.32608 4.02678 10.5979 4.96447 11.5355C5.90215 12.4732 7.17392 13 8.5 13C9.82608 13 11.0979 12.4732 12.0355 11.5355C12.9732 10.5979 13.5 9.32608 13.5 8C13.5 6.67392 12.9732 5.40215 12.0355 4.46447Z',
      innerFillOpacity: undefined,
      innerStyle: { filter: 'drop-shadow(0 0 2px rgba(245, 208, 126, 0.5))' },
      strokeOpacity: '0.3',
      strokeWidth: '2',
    }))
    .with('on-deck', () => ({
      viewBox: '0 0 24 24',
      color: mappedColors.primary.glow,
      innerPath:
        'M12 4C14.1217 4 16.1566 4.84285 17.6569 6.34315C19.1571 7.84344 20 9.87827 20 12C20 14.1217 19.1571 16.1566 17.6569 17.6569C16.1566 19.1571 14.1217 20 12 20C9.87827 20 7.84344 19.1571 6.34315 17.6569C4.84285 16.1566 4 14.1217 4 12C4 9.87827 4.84285 7.84344 6.34315 6.34315C7.84344 4.84285 9.87827 4 12 4Z',
      outerPath:
        'M12.4961 2.0127C14.9676 2.13537 17.3132 3.17061 19.0713 4.92871C20.9467 6.80407 22 9.34784 22 12L21.9873 12.4961C21.8646 14.9676 20.8294 17.3132 19.0713 19.0713C17.3132 20.8294 14.9676 21.8646 12.4961 21.9873L12 22C9.34784 22 6.80407 20.9467 4.92871 19.0713C3.05335 17.1959 2 14.6522 2 12C2 9.34784 3.05335 6.80407 4.92871 4.92871C6.80407 3.05335 9.34784 2 12 2L12.4961 2.0127Z',
      innerFillOpacity: '0.9',
      innerStyle: undefined,
      strokeOpacity: '0.4',
      strokeWidth: '4',
    }))
    .exhaustive()

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox={config.viewBox}
      fill="none"
      className={classNames(styles.pulsingDot, className)}
    >
      <path d={config.innerPath} fill={config.color} fillOpacity={config.innerFillOpacity} style={config.innerStyle} />
      <path
        d={config.outerPath}
        stroke={config.color}
        strokeOpacity={config.strokeOpacity}
        strokeWidth={config.strokeWidth}
      />
    </svg>
  )
}
